import { SortableContext } from "@dnd-kit/sortable";
import { TableParamTypeEnum } from "@selector/shared/graphql";
import { type Maybe } from "purify-ts";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { formValueSelector, reduxForm } from "redux-form";
import { ActionButtonIntent, DialogComponent, HStack, Modal, Tag, TagIntent, TriggerButton, VStack } from "widgets";
import { useOverlay } from "widgets/hooks";
import { moveItemsInArray } from "widgets/utils";

import { type ISelectOption, simpleFieldValidation } from "@/app-utils/form.ts";
import { getGenomeTypeOptions } from "@/app-utils/playbook.ts";
import {
  type ActivityParameter,
  type GenomeConstraints,
  type NfPlaybookParam,
  WorkflowParameterTypeEnum,
} from "@/graphql.ts";
import { WorkflowParameterField } from "@/modules/workflow/components/parameters/WorkflowParameterField.tsx";
import { extractValue } from "@/modules/workflow/utils/generateWorkflow.ts";
import type { AllParametersTypes, WorkflowErrorParameter, WorkflowParameter } from "@/modules/workflow/utils/types.ts";
import {
  type ActivityInstanceExtendedParameterWithActivity,
  buildFieldProps,
  isInvalidSampleFileParameter,
} from "@/redux/modules/playbook-admin.ts";
import { type IAppState } from "@/redux/reducer.js";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { GREY_COLOR, SPECIAL_COLOR } from "@/settings/colors-settings.ts";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";
import CheckboxField, { CheckboxReduxFormField } from "@/ui-modules/core/redux-form/CheckboxField.tsx";
import ReactSelectField, { ReactSelectReduxFormField } from "@/ui-modules/core/redux-form/ReactSelectField.tsx";

import { SortableItem, SortableList } from "../../core/forms/Sortable.js";
import style from "../../core/forms/styles/Sortable.module.scss";
import InputField, { InputReduxFormField } from "../../core/redux-form/InputField.js";

interface P2ParamsConfiguratorProps {
  lockedVisibilityParams: ActivityInstanceExtendedParameterWithActivity[];
  p2ParamConfigurations: ActivityInstanceExtendedParameterWithActivity[];
  setP2ParamConfigurations: (p2ParamConfigurations: ActivityInstanceExtendedParameterWithActivity[]) => void;
}

export interface NfParamConfiguration extends NfPlaybookParam {
  id?: string; // needed for the sortable context
  defaultWorkflowValue?: string;
}

const P2ParamsConfigurator: FC<P2ParamsConfiguratorProps> = (props) => {
  return (
    <div className="experiment-info-screen">
      <FormInputGroup>
        <FormLabel>
          {`${
            props.p2ParamConfigurations.length ? props.p2ParamConfigurations.length : "No "
          } workflow parameter(s) available`}
        </FormLabel>
        <FormInputWidget>
          <div className={"input-ordering"}>
            <SortableList
              onDragEnd={({ active, over }) => {
                props.setP2ParamConfigurations(
                  moveItemsInArray<ActivityInstanceExtendedParameterWithActivity, any, any>(
                    props.p2ParamConfigurations,
                    active,
                    over,
                  ),
                );
              }}
            >
              <SortableContext items={props.p2ParamConfigurations.map((p2ParamConfig) => p2ParamConfig.id)}>
                {props.p2ParamConfigurations.map((p2ParamConfig, index) => (
                  <Item
                    key={`item-${index}`}
                    index={index}
                    p2ParamConfig={p2ParamConfig}
                    p2ParamConfigurations={props.p2ParamConfigurations}
                    setP2ParamConfigurations={props.setP2ParamConfigurations}
                    lockedVisibilityParams={props.lockedVisibilityParams}
                  />
                ))}
              </SortableContext>
            </SortableList>
          </div>
        </FormInputWidget>
      </FormInputGroup>
    </div>
  );
};

interface ItemProps {
  p2ParamConfig: ActivityInstanceExtendedParameterWithActivity;
  index: number;
  setP2ParamConfigurations: (confs: ActivityInstanceExtendedParameterWithActivity[]) => void;
  p2ParamConfigurations: ActivityInstanceExtendedParameterWithActivity[];
  lockedVisibilityParams: ActivityInstanceExtendedParameterWithActivity[];
}

const Item: FC<ItemProps> = ({
  p2ParamConfig,
  index,
  setP2ParamConfigurations,
  p2ParamConfigurations,
  lockedVisibilityParams,
}) => {
  const { overlayProps, state } = useOverlay();

  const onConfigChanged = (p2ParamConfig: ActivityInstanceExtendedParameterWithActivity) => {
    setP2ParamConfigurations(
      p2ParamConfigurations.map((p) =>
        p.activity.identifier === p2ParamConfig.activity.identifier &&
        p.parameterDefinition.id === p2ParamConfig.parameterDefinition.id
          ? p2ParamConfig
          : p,
      ),
    );
  };

  return (
    <>
      <SortableItem index={index} removeDisabled id={p2ParamConfig.id}>
        <P2ParamsConfigItem
          p2ParamConfig={p2ParamConfig}
          onVisibleStateChanged={() => {
            setP2ParamConfigurations(
              p2ParamConfigurations.map((p) =>
                // identify by both param and activity IDs
                p.parameterDefinition.id === p2ParamConfig.parameterDefinition.id &&
                p.activity.identifier === p2ParamConfig.activity.identifier
                  ? { ...p, staged: !p.staged }
                  : p,
              ),
            );
          }}
          lockedVisibility={
            !!lockedVisibilityParams.find(
              (p) =>
                p?.activity.identifier === p2ParamConfig?.activity.identifier &&
                p?.parameterDefinition.id === p2ParamConfig?.parameterDefinition.id,
            ) ||
            // if a mandatory value has no default, we force it to stay staged
            (!p2ParamConfig.parameterDefinition.optional && !extractValue(p2ParamConfig.workflowParameter))
          }
        />
        <>
          <P2ParamsTags p2ParamConfig={p2ParamConfig} />
          <TriggerButton
            label="Configure Parameter"
            tooltip={"Configure how this parameter will be shown as input during the Playbook execution "}
            onPress={() => state.open()}
            icon={"edit"}
          />
        </>
      </SortableItem>
      <Modal state={state} {...overlayProps}>
        <ConfigureP2ParamFormDialog
          p2ParamConfig={p2ParamConfig}
          closeFn={state.close}
          onConfigChanged={onConfigChanged}
        />
      </Modal>
    </>
  );
};

interface P2ParamsConfigItemProps {
  p2ParamConfig: ActivityInstanceExtendedParameterWithActivity;
  onVisibleStateChanged?: () => void;
  lockedVisibility: boolean;
}

const P2ParamsConfigItem: FC<P2ParamsConfigItemProps> = ({
  p2ParamConfig,
  onVisibleStateChanged,
  lockedVisibility,
}) => {
  return (
    <>
      <P2ParamsVisibilityToggle
        p2ParamConfig={p2ParamConfig}
        onVisibleStateChanged={onVisibleStateChanged}
        lockedVisibility={lockedVisibility}
      />
      <span className={style.label}>
        {p2ParamConfig.parameterDefinition.label}
        <div className={style.sub_label} style={{ opacity: p2ParamConfig.parameterDefinition.description ? 1 : 0.5 }}>
          {p2ParamConfig.parameterDefinition.description || "No description"}
        </div>
      </span>
      <div style={{ paddingRight: "10px" }}>
        <Tag intentColor={TagIntent.Outlined} style={{ borderRadius: "2px" }}>
          {p2ParamConfig.activity.label}
        </Tag>
      </div>
    </>
  );
};

const P2ParamsTags: FC<{ p2ParamConfig: ActivityInstanceExtendedParameterWithActivity }> = ({ p2ParamConfig }) => {
  return (
    p2ParamConfig.parameterDefinition.type && (
      <Tag
        tooltip={{ title: p2ParamConfig.optional ? "optional" : "mandatory" }}
        intentColor={p2ParamConfig.optional ? TagIntent.Success : TagIntent.Secondary}
      >
        {getParamTypeLabel(p2ParamConfig.parameterDefinition)}
      </Tag>
    )
  );
};

interface P2ParamsVisibilityToggleProps {
  p2ParamConfig: ActivityInstanceExtendedParameterWithActivity;
  onVisibleStateChanged?: () => void;
  lockedVisibility: boolean;
}

const P2ParamsVisibilityToggle: FC<P2ParamsVisibilityToggleProps> = ({
  p2ParamConfig,
  onVisibleStateChanged,
  lockedVisibility,
}) => {
  const hidden = !p2ParamConfig.staged;
  return (
    <TriggerButton
      label={"Toggle visibility"}
      tooltip={
        lockedVisibility
          ? "You cannot change the visibility because this parameter is selected as a mapping target or it is mandatory without a default."
          : "Toggle the visibility of this parameter in the Playbook"
      }
      onPress={onVisibleStateChanged}
      isDisabled={lockedVisibility}
      icon={hidden ? "visibility_off" : "visibility"}
      style={{ color: hidden ? GREY_COLOR.WHITE_2 : SPECIAL_COLOR.ACTIVE }}
    />
  );
};

interface ConfigureP2ParamFormDialogProps {
  p2ParamConfig: ActivityInstanceExtendedParameterWithActivity;
  onConfigChanged: (p2ParamConfig: ActivityInstanceExtendedParameterWithActivity) => void;
  closeFn: () => void;
}

enum FieldName {
  label = "label",
  description = "description",
  optional = "optional",
  defaultValue = "defaultValue",
  allowedGenomeTypes = "allowedGenomeTypes",
}

interface ConfigureP2ParamFormState {
  [FieldName.label]: string;
  [FieldName.description]: string;
  [FieldName.optional]: boolean;
  [FieldName.defaultValue]: WorkflowParameter;
  [FieldName.allowedGenomeTypes]: ISelectOption[];
}

const configureP2ParamFormName = "configureP2Param";

const ConfigureP2ParamFormDialog = reduxForm<ConfigureP2ParamFormState, ConfigureP2ParamFormDialogProps>({
  form: configureP2ParamFormName,
})((props) => {
  useEffect(() => {
    // init the local state object whch is the base for the default value setting
    const workflowParameter: WorkflowParameter = props.p2ParamConfig.workflowParameter;

    props.initialize({
      [FieldName.label]: props.p2ParamConfig.parameterDefinition.label,
      [FieldName.description]: props.p2ParamConfig.parameterDefinition.description,
      [FieldName.optional]: props.p2ParamConfig.optional,
      [FieldName.defaultValue]: workflowParameter,
      [FieldName.allowedGenomeTypes]: props.p2ParamConfig.genomeConstraints?.allowedTypes?.map((t) =>
        getGenomeTypeOptions().find((o) => o.value === t),
      ),
    });
  }, []);

  // the list is overhead, but for compatibility reasons with checkSingleProcessorActivityParameter we stick with a list
  const [error, setError] = useState<WorkflowErrorParameter>();
  const [internalError, setInternalError] = useState<boolean>(false);

  // extract the input type so that we can display the correct input for defining default value
  const p2Params = useSelector<IAppState, ActivityInstanceExtendedParameterWithActivity[] | undefined>(
    (state) => state.playbookAdminState.playbookConfigState.p2Params,
  );

  const sampleTypeId = useSelector<IAppState, number | undefined>(
    (state) => state.playbookAdminState.playbookConfigState.sampleTypeId,
  );

  const workflowParameter = useSelector<IAppState, ActivityInstanceExtendedParameterWithActivity[] | undefined>(
    (state) => {
      const valueSelector = formValueSelector(configureP2ParamFormName);
      return valueSelector(state, FieldName.defaultValue);
    },
  );

  useEffect(() => {
    // validate the current workflowParameter value
    const p2ParamConfig = props.p2ParamConfig;
    if (workflowParameter) {
      const result = workflowParameter.schema?.safeParse(workflowParameter.values.extractNullable());
      let newError = undefined;

      // if this is a mandatory sample-file-attribute, ensure that there is a value set for the attribute
      if (isInvalidSampleFileParameter(workflowParameter, props.p2ParamConfig)) {
        newError = {
          type: "parameter",
          nodeId: p2ParamConfig.activity.identifier,
          parameterId: p2ParamConfig.parameterDefinition.id,
          message: "You must select an attribute default because this parameter is mandatory",
          code: undefined,
        } satisfies WorkflowErrorParameter;
      } else if (result?.success) {
        newError = undefined;
      } else {
        newError = {
          type: "parameter",
          nodeId: p2ParamConfig.activity.identifier,
          parameterId: p2ParamConfig.parameterDefinition.id,
          message: result?.error.issues[0].message,
          code: result?.error.issues[0].params?.code ?? result?.error.issues[0].code,
        } satisfies WorkflowErrorParameter;
      }

      setError(newError);
    }
  }, [workflowParameter]);

  const selectedGenomes = useMemo(() => {
    const genomeIds = p2Params
      ?.filter((param) => param.parameterDefinition.type === WorkflowParameterTypeEnum.Genome)
      .map((param) => param.workflowParameter?.values.extractNullable())
      .flatMap((p) => p)
      .filter((v): v is number => typeof v === "number");

    const uniqueNumbers = [...new Set(genomeIds)];
    return uniqueNumbers;
  }, [p2Params]);

  const setValue = useCallback(
    (values: Maybe<AllParametersTypes>) => {
      props.change(FieldName.defaultValue, { ...workflowParameter, values: values });
    },
    [workflowParameter],
  );

  const modifiedParamConfig = useMemo(() => {
    // For genome params, adapt the genome type restriction in order to consistently provide all possible genome types during config
    let modifiedParamConfig = { ...props.p2ParamConfig };
    if (props.p2ParamConfig.parameterDefinition.type === WorkflowParameterTypeEnum.Genome) {
      // If we decide to filter down the genome table to the selected genome types instead,
      // use valueSelector to get current values from FieldName.allowedGenomeTypes
      const selectedGenomeTypeRestriction = getGenomeTypeOptions();
      const newGenomeConstraints = {
        ...props.p2ParamConfig.genomeConstraints,
        allowedTypes: selectedGenomeTypeRestriction.map((r) => r.value),
      };

      modifiedParamConfig = {
        ...props.p2ParamConfig,
        genomeConstraints: newGenomeConstraints as GenomeConstraints,
        parameterDefinition: {
          ...props.p2ParamConfig.parameterDefinition,
          genomeConstraints: newGenomeConstraints,
        } as ActivityParameter,
      };
    }
    return modifiedParamConfig;
  }, [props.p2ParamConfig]);

  return (
    <DialogComponent
      title="Configure Your Playbook Input"
      closeFn={props.closeFn}
      mainButton={{
        label: "Done",
        onPress: props.handleSubmit(handleStoreParamConfig),
        intent: ActionButtonIntent.Primary,
        isDisabled: !!error || internalError,
      }}
      errorCallout={{ message: props.error }}
    >
      <VStack>
        <InputReduxFormField
          componentClass="input"
          name={FieldName.label}
          label={"Label"}
          component={InputField}
          validate={[simpleFieldValidation]}
        />
        <InputReduxFormField
          componentClass="input"
          name={FieldName.description}
          label={"Description"}
          component={InputField}
        />
        <CheckboxReduxFormField
          name={FieldName.optional}
          label="Optional"
          disabled={!props.p2ParamConfig.parameterDefinition.optional}
          component={CheckboxField}
        />
        {props.p2ParamConfig.parameterDefinition.type === WorkflowParameterTypeEnum.Genome && (
          <ReactSelectReduxFormField
            name={FieldName.allowedGenomeTypes}
            formGroupClassName="input-field-playbook-context"
            horizontalLayout={false}
            isClearable={true}
            isSearchable={false}
            label={<HStack>Selectable genome types</HStack>}
            placeholder="Choose the genome types available for users when selecting genomes during Playbook execution"
            component={ReactSelectField}
            options={getGenomeTypeOptions()}
            getOptionLabel={(option) => option.label}
            isDisabled={false}
            isMulti={true}
          />
        )}
        {workflowParameter && (
          <WorkflowParameterField
            key={`${props.p2ParamConfig.activity.identifier}-${props.p2ParamConfig.parameterDefinition.id}`}
            parameter={buildFieldProps(modifiedParamConfig)}
            sampleTypeId={sampleTypeId}
            error={error}
            value={workflowParameter?.values?.extractNullable()}
            setValue={(values) => {
              setValue(values);
            }}
            validateSingleParameter={() => {}} // we do not use this here because it is called to early, before the value is updated in the redux state
            selectedGenomesIds={selectedGenomes}
            onInternalErrorChange={setInternalError}
            isPlaybookContext={true}
          />
        )}
      </VStack>
    </DialogComponent>
  );
});
function handleStoreParamConfig(values, _dispatch: ReduxDispatch, props: ConfigureP2ParamFormDialogProps) {
  const workflowParameter = {
    ...props.p2ParamConfig.workflowParameter,
    values: (values[FieldName.defaultValue] as WorkflowParameter).values,
  };
  const realNewValue = extractValue(workflowParameter);

  const genomeConstraints =
    props.p2ParamConfig.parameterDefinition.type === WorkflowParameterTypeEnum.Genome
      ? {
          ...props.p2ParamConfig.genomeConstraints,
          allowedTypes: values[FieldName.allowedGenomeTypes]?.map((g: ISelectOption) => g.value),
        }
      : undefined;

  props.onConfigChanged({
    ...props.p2ParamConfig,
    // if the value was removed and the parameter is mandatory, force the parameter to staged (it must be visible)
    staged: !realNewValue && !props.p2ParamConfig.parameterDefinition.optional ? true : props.p2ParamConfig.staged,
    optional: values[FieldName.optional],
    parameterDefinition: {
      ...props.p2ParamConfig.parameterDefinition,
      label: values[FieldName.label]?.trim(),
      description: values[FieldName.description]?.trim(),
    },
    genomeConstraints: genomeConstraints,
    workflowParameter: workflowParameter,
  });
  props.closeFn();
}

const getParamTypeLabel = (p2ParamConfig: ActivityParameter) => {
  switch (p2ParamConfig.type) {
    case WorkflowParameterTypeEnum.Text:
      // how to build this correctly in TS? Or rather suppress?
      return p2ParamConfig.stringConstraints?.controlledValues ? "Controlled Text" : "Free Text";
    case WorkflowParameterTypeEnum.Number:
      return p2ParamConfig.numberConstraints?.controlledValues ? "Controlled Number" : "Free Number";
    case WorkflowParameterTypeEnum.Boolean:
      return "Boolean";
    case WorkflowParameterTypeEnum.Date:
      return "Date";
    case WorkflowParameterTypeEnum.Sample:
      return "Sample";
    case WorkflowParameterTypeEnum.GenomicRegion:
      return "Genomic region";
    case WorkflowParameterTypeEnum.File:
      return "File";
      break;
    case WorkflowParameterTypeEnum.Genome:
      return "Genome";
      break;
    case WorkflowParameterTypeEnum.Table:
      return p2ParamConfig.tableType === TableParamTypeEnum.Variant ? "Variant" : "Coordinate";
      break;
    default:
      return "Unknown";
  }
};

export default P2ParamsConfigurator;
